import { activityDescriptor } from '../definitions/activity';
import { activityTaxonomyDescriptor } from '../definitions/activityTaxonomy';
import { attritionRateDescriptor } from '../definitions/attritionRate';
import { companyDescriptor } from '../definitions/company';
import { competitorsDescriptor } from '../definitions/competitors';
import { contingentWorkersDescriptor } from '../definitions/contingentWorkers';
import { customSkillDescriptor } from '../definitions/customSkill';
import { dataAdjustmentsDescriptor } from '../definitions/dataAdjustments';
import { dateRangeDescriptor } from '../definitions/dateRange';
import { educationDescriptor } from '../definitions/education';
import { employmentRelationshipDescriptor } from '../definitions/employmentRelationship';
import { ethnicityDescriptor } from '../definitions/ethnicity';
import { externallyOwnedDescriptor } from '../definitions/externallyOwned';
import { flightRiskDescriptor } from '../definitions/flightRisk';
import { founderEthnicityDescriptor } from '../definitions/founderEthnicity';
import { founderGenderDescriptor } from '../definitions/founderGender';
import { founderInfoDescriptor } from '../definitions/founderInfo';
import { founderPreviousCompanyDescriptor } from '../definitions/founderPreviousCompany';
import { founderPreviousSchoolDescriptor } from '../definitions/founderPreviousSchool';
import { fundingInfoDescriptor } from '../definitions/fundingInfo';
import { fundingStageDescriptor } from '../definitions/fundingStage';
import { genderDescriptor } from '../definitions/gender';
import { geographyDescriptor } from '../definitions/geography';
import { geographyTaxonomyDescriptor } from '../definitions/geographyTaxonomy';
import { graduationYearDescriptor } from '../definitions/graduationYear';
import { growthRateDescriptor } from '../definitions/growthRate';
import { headcountDescriptor } from '../definitions/headcount';
import { hiringRateDescriptor } from '../definitions/hiringRate';
import { hqLocationDescriptor } from '../definitions/hqLocation';
import { industryDescriptor } from '../definitions/industry';
import { inflowDescriptor } from '../definitions/inflow';
import { keywordsDescriptor } from '../definitions/keywords';
import { lastFundingAmountDescriptor } from '../definitions/lastFundingAmount';
import { lastFundingYearDescriptor } from '../definitions/lastFundingYear';
import { metricModeDescriptor } from '../definitions/metricMode';
import { nameDescriptor } from '../definitions/name';
import { outflowDescriptor } from '../definitions/outflow';
import { overallRatingDescriptor } from '../definitions/overallRating';
import { payDescriptor } from '../definitions/pay';
import { postDateDescriptor } from '../definitions/postDate';
import { postingsSourceDescriptor } from '../definitions/postingsSource';
import { prestigeDescriptor } from '../definitions/prestige';
import { publicDescriptor } from '../definitions/public';
import { remoteSuitabilityDescriptor } from '../definitions/remoteSuitability';
import { remoteTypeDescriptor } from '../definitions/remoteType';
import { roleDescriptor } from '../definitions/role';
import { roleTaxonomyDescriptor } from '../definitions/roleTaxonomy';
import { salaryDescriptor } from '../definitions/salary';
import { schoolDescriptor } from '../definitions/school';
import { seniorityDescriptor } from '../definitions/seniority';
import { skillDescriptor } from '../definitions/skill';
import { skillTaxonomyDescriptor } from '../definitions/skillTaxonomy';
import { snapshotDateDescriptor } from '../definitions/snapshotDate';
import { tenureDescriptor } from '../definitions/tenure';
import { titleDescriptor } from '../definitions/title';
import { totalFundingAmountDescriptor } from '../definitions/totalFundingAmount';
import { valuationDescriptor } from '../definitions/valuation';
import { workScheduleDescriptor } from '../definitions/workSchedule';
import { yearFoundedDescriptor } from '../definitions/yearFounded';
import { yearsOfExperienceDescriptor } from '../definitions/yearsOfExperience';
import { type Filter, FilterId } from '../types/filters';
import {
  type DefinitionMapOf,
  type FilterDescriptor,
  wrapAsMulti,
} from './descriptor';
import type { FilterHandler } from './types';

const FILTER_DESCRIPTORS = [
  companyDescriptor,
  dataAdjustmentsDescriptor,
  dateRangeDescriptor,
  postDateDescriptor,
  educationDescriptor,
  ethnicityDescriptor,
  flightRiskDescriptor,
  genderDescriptor,
  geographyDescriptor,
  industryDescriptor,
  keywordsDescriptor,
  nameDescriptor,
  overallRatingDescriptor,
  postingsSourceDescriptor,
  metricModeDescriptor,
  prestigeDescriptor,
  remoteSuitabilityDescriptor,
  remoteTypeDescriptor,
  roleDescriptor,
  roleTaxonomyDescriptor,
  schoolDescriptor,
  skillTaxonomyDescriptor,
  activityTaxonomyDescriptor,
  geographyTaxonomyDescriptor,
  seniorityDescriptor,
  skillDescriptor,
  customSkillDescriptor,
  activityDescriptor,
  snapshotDateDescriptor,
  titleDescriptor,
  contingentWorkersDescriptor,
  employmentRelationshipDescriptor,
  workScheduleDescriptor,
  graduationYearDescriptor,
  payDescriptor,
  yearsOfExperienceDescriptor,
  attritionRateDescriptor,
  growthRateDescriptor,
  hiringRateDescriptor,
  headcountDescriptor,
  inflowDescriptor,
  outflowDescriptor,
  salaryDescriptor,
  tenureDescriptor,
  yearFoundedDescriptor,
  competitorsDescriptor,
  hqLocationDescriptor,
  externallyOwnedDescriptor,
  publicDescriptor,
  founderInfoDescriptor,
  founderEthnicityDescriptor,
  founderGenderDescriptor,
  founderPreviousCompanyDescriptor,
  founderPreviousSchoolDescriptor,
  fundingInfoDescriptor,
  fundingStageDescriptor,
  lastFundingAmountDescriptor,
  lastFundingYearDescriptor,
  totalFundingAmountDescriptor,
  valuationDescriptor,
] as const;

type RegisteredDescriptor = (typeof FILTER_DESCRIPTORS)[number];

export type AllFilterDefinitionsMapType = DefinitionMapOf<RegisteredDescriptor>;

export type AllFilterHandlersMapType = {
  [Id in keyof AllFilterDefinitionsMapType]: FilterHandler<
    AllFilterDefinitionsMapType[Id]
  >;
};

const registeredDescriptors: readonly FilterDescriptor[] = FILTER_DESCRIPTORS;

const multiPairs = registeredDescriptors.flatMap((descriptor) =>
  descriptor.multi === undefined
    ? []
    : [{ descriptor, multi: descriptor.multi }]
);

const multiEntries = multiPairs.map(({ descriptor, multi }) =>
  wrapAsMulti(descriptor, multi)
);

const registryEntries: {
  id: FilterId;
  definition: Filter;
  handler: FilterHandler<Filter>;
}[] = [...registeredDescriptors, ...multiEntries];

const duplicateIds = registryEntries
  .map(({ id }) => id)
  .filter((id, index, ids) => ids.indexOf(id) !== index);
if (duplicateIds.length > 0) {
  throw new Error(
    `Filter ids registered more than once: ${duplicateIds.join(', ')}`
  );
}

export const ALL_FILTER_DEFINITIONS = Object.fromEntries(
  registryEntries.map(({ id, definition }) => [id, definition])
) as Readonly<AllFilterDefinitionsMapType>;

const ALL_FILTER_HANDLERS = Object.fromEntries(
  registryEntries.map(({ id, handler }) => [id, handler])
) as Readonly<AllFilterHandlersMapType>;

const isSegmented = ({ multi }: (typeof multiPairs)[number]) =>
  multi.segmented === true;

export const MULTI_FILTER_ID_BY_FILTER_ID: Partial<Record<FilterId, FilterId>> =
  Object.fromEntries(
    multiPairs
      .filter((pair) => !isSegmented(pair))
      .map(({ descriptor, multi }) => [descriptor.id, multi.id])
  );

const segmentedPairs = multiPairs.filter(isSegmented);

export const SUB_FILTER_IDS = new Set(
  segmentedPairs.map(({ multi }) => multi.id)
);

/**
 * Base screener filter IDs that should only exist as wrapped filters inside their MULTI counterparts.
 * These filters have definitions in ALL_FILTER_DEFINITIONS (used as templates for wrapped filters),
 * but should never appear in effectiveFilters as standalone filters in screener v2.
 * They are excluded from effectiveFilters in useEffectiveFilters hook.
 */
export const WRAPPED_ONLY_FILTER_IDS = new Set(
  segmentedPairs.map(({ descriptor }) => descriptor.id)
);

type FilterFormat = { text: string; type: 'prefix' | 'suffix' | 'none' };

const BASE_FILTER_FORMATS: Partial<Record<FilterId, FilterFormat>> = {
  [FilterId.ATTRITION_RATE]: { text: '%', type: 'suffix' },
  [FilterId.GROWTH_RATE]: { text: '%', type: 'suffix' },
  [FilterId.HIRING_RATE]: { text: '%', type: 'suffix' },
  [FilterId.HEADCOUNT]: { text: '', type: 'none' },
  [FilterId.INFLOW]: { text: '', type: 'none' },
  [FilterId.OUTFLOW]: { text: '', type: 'none' },
  [FilterId.SALARY]: { text: '$', type: 'prefix' },
  [FilterId.TENURE]: { text: 'yr', type: 'suffix' },
  [FilterId.LAST_FUNDING_AMOUNT]: { text: '$', type: 'prefix' },
  [FilterId.TOTAL_FUNDING_AMOUNT]: { text: '$', type: 'prefix' },
  [FilterId.VALUATION]: { text: '$', type: 'prefix' },
};

export const FILTER_FORMAT_MAP: Partial<Record<FilterId, FilterFormat>> = {
  ...BASE_FILTER_FORMATS,
  ...Object.fromEntries(
    segmentedPairs.flatMap(({ descriptor, multi }) => {
      const format = BASE_FILTER_FORMATS[descriptor.id];
      return format === undefined ? [] : [[multi.id, format]];
    })
  ),
};

export const DEFAULT_ACTIVE_FILTERS = Object.values(
  ALL_FILTER_DEFINITIONS
).filter((definition) => definition.isActive);

export function findFilterById<T extends FilterId>(
  filters: Filter[],
  filterId: T
): AllFilterDefinitionsMapType[T] | undefined {
  return filters.find(
    (filter): filter is AllFilterDefinitionsMapType[T] => filter.id === filterId
  );
}

export function getFilterDefinition<T extends FilterId>(
  filterId: T
): AllFilterDefinitionsMapType[T] | undefined {
  return ALL_FILTER_DEFINITIONS[
    filterId as keyof typeof ALL_FILTER_DEFINITIONS
  ] as AllFilterDefinitionsMapType[T] | undefined;
}

export function getFilterHandler<T extends FilterId>(
  filterId: T
):
  | (T extends keyof AllFilterHandlersMapType
      ? AllFilterHandlersMapType[T]
      : undefined)
  | undefined {
  return ALL_FILTER_HANDLERS[filterId as keyof typeof ALL_FILTER_HANDLERS] as
    | (T extends keyof AllFilterHandlersMapType
        ? AllFilterHandlersMapType[T]
        : undefined)
    | undefined;
}

const _ensureEveryFilterIdRegistered: Record<FilterId, Filter> =
  ALL_FILTER_DEFINITIONS;

// A multi the descriptor types can't model resolves to `never`, which the check above lets through.
type UnmodelledFilterId = {
  [Id in keyof AllFilterDefinitionsMapType]: [
    AllFilterDefinitionsMapType[Id],
  ] extends [never]
    ? Id
    : never;
}[keyof AllFilterDefinitionsMapType];
const _ensureEveryFilterIdTyped: [UnmodelledFilterId] extends [never]
  ? true
  : UnmodelledFilterId = true;
